/**
 * 学習計画型定義（フロントエンド）
 */

// Enum定義
export type PlanFrequency = 'WEEKLY' | 'BIWEEKLY' | 'MONTHLY' | 'CUSTOM'
export type TestDifficulty = 'EASY' | 'MEDIUM' | 'HARD'
export type PlanStatus = 'DRAFT' | 'ACTIVE' | 'PAUSED' | 'COMPLETED' | 'CANCELLED'
export type ScheduledTestStatus =
  | 'PENDING'
  | 'GENERATED'
  | 'APPROVED'
  | 'PUBLISHED'
  | 'COMPLETED'
  | 'CANCELLED'
export type ReportType = 'INTERIM' | 'FINAL'
export type LearningPlanTargetType = 'GROUP' | 'DEPARTMENT' | 'POSITION' | 'INDIVIDUAL'

// ラベル定義
export const PLAN_FREQUENCY_LABELS: Record<PlanFrequency, string> = {
  WEEKLY: '毎週',
  BIWEEKLY: '隔週',
  MONTHLY: '毎月',
  CUSTOM: 'カスタム',
}

export const TEST_DIFFICULTY_LABELS: Record<TestDifficulty, string> = {
  EASY: '易しい',
  MEDIUM: '普通',
  HARD: '難しい',
}

export const PLAN_STATUS_LABELS: Record<PlanStatus, string> = {
  DRAFT: '下書き',
  ACTIVE: '実行中',
  PAUSED: '一時停止',
  COMPLETED: '完了',
  CANCELLED: 'キャンセル',
}

export const PLAN_STATUS_COLORS: Record<
  PlanStatus,
  'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'
> = {
  DRAFT: 'default',
  ACTIVE: 'success',
  PAUSED: 'warning',
  COMPLETED: 'info',
  CANCELLED: 'error',
}

export const SCHEDULED_TEST_STATUS_LABELS: Record<ScheduledTestStatus, string> = {
  PENDING: '生成待ち',
  GENERATED: '承認待ち',
  APPROVED: '承認済み',
  PUBLISHED: '公開中',
  COMPLETED: '完了',
  CANCELLED: 'キャンセル',
}

export const SCHEDULED_TEST_STATUS_COLORS: Record<
  ScheduledTestStatus,
  'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'
> = {
  PENDING: 'default',
  GENERATED: 'warning',
  APPROVED: 'info',
  PUBLISHED: 'primary',
  COMPLETED: 'success',
  CANCELLED: 'error',
}

// 対象タイプラベル
export const TARGET_TYPE_LABELS: Record<LearningPlanTargetType, string> = {
  GROUP: 'グループ',
  DEPARTMENT: '組織',
  POSITION: '役職',
  INDIVIDUAL: '個人',
}

// 対象選択の型定義
export interface LearningPlanTargetInput {
  type: LearningPlanTargetType
  ids: string[]
}

// 対象選択レスポンス
export interface LearningPlanTargetResponse {
  id: string
  targetType: LearningPlanTargetType
  targetValue: string
  displayLabel?: string
}

// 対象者サマリー
export interface TargetsSummary {
  totalUsers: number
  breakdown: {
    group: { count: number; names: string[] }
    department: { count: number; names: string[] }
    position: { count: number; names: string[] }
    individual: { count: number; names: string[] }
  }
}

// レスポンス型
export interface LearningPlanResponse {
  id: string
  tenantId: string
  title: string
  description: string | null
  complianceRuleId: string
  userGroupId: string | null // 旧方式（後方互換）
  frequency: PlanFrequency
  customIntervalDays: number | null
  questionCount: number
  difficulty: TestDifficulty
  passingScore: number
  startDate: string
  endDate: string
  testGenerationDaysBefore: number
  requireApproval: boolean
  autoPublish: boolean
  status: PlanStatus
  isActive: boolean
  createdBy: string
  createdAt: string
  updatedAt: string
  complianceRule?: {
    id: string
    title: string
    category: string
  }
  userGroup?: {
    id: string
    name: string
    memberCount?: number
  }
  creator?: {
    id: string
    name: string
  }
  // 複合対象選択（新方式）
  targets?: LearningPlanTargetResponse[]
  targetsSummary?: TargetsSummary
  scheduledTestsCount?: number
  completedTestsCount?: number
  nextScheduledDate?: string | null
}

export interface ScheduledTestResponse {
  id: string
  learningPlanId: string
  testId: string | null
  groupEnrollmentId: string | null
  scheduledDate: string
  generationDate: string
  dueDate: string
  sequenceNumber: number
  status: ScheduledTestStatus
  approvedBy: string | null
  approvedAt: string | null
  rejectedReason: string | null
  generatedAt: string | null
  publishedAt: string | null
  createdAt: string
  updatedAt: string
  test?: {
    id: string
    title: string
    passingScore: number
  }
  approver?: {
    id: string
    name: string
  }
  progress?: {
    totalParticipants: number
    completedCount: number
    passedCount: number
    averageScore: number | null
  }
}

export interface LearningPlanProgressResponse {
  learningPlanId: string
  totalScheduledTests: number
  completedTests: number
  currentTestSequence: number | null
  overallProgress: number
  participantStats: ParticipantStats[]
  testResults: ScheduledTestResult[]
  averageScoreTrend: ScoreTrendPoint[]
}

export interface ParticipantStats {
  userId: string
  userName: string
  testsCompleted: number
  testsTotal: number
  averageScore: number | null
  lastTestDate: string | null
  trend: 'improving' | 'stable' | 'declining' | 'unknown'
}

export interface ScheduledTestResult {
  scheduledTestId: string
  sequenceNumber: number
  scheduledDate: string
  status: ScheduledTestStatus
  completedCount: number
  totalParticipants: number
  passedCount: number
  averageScore: number | null
}

export interface ScoreTrendPoint {
  sequenceNumber: number
  scheduledDate: string
  averageScore: number | null
  participantCount: number
}

export interface LearningPlanReportResponse {
  id: string
  learningPlanId: string
  reportType: ReportType
  periodStart: string
  periodEnd: string
  statistics: ReportStatistics
  generatedAt: string
  generatedBy: string | null
  createdAt: string
  learningPlan?: {
    id: string
    title: string
  }
  generator?: {
    id: string
    name: string
  }
}

export interface ReportStatistics {
  totalTests: number
  completedTests: number
  averageScore: number | null
  passRate: number | null
  participantStats: ParticipantStats[]
  scoreDistribution: ScoreDistributionBucket[]
  improvementTrend: ScoreTrendPoint[]
}

export interface ScoreDistributionBucket {
  range: string
  count: number
  percentage: number
}

// リクエスト型
export interface CreateLearningPlanRequest {
  title: string
  description?: string
  complianceRuleId: string
  userGroupId?: string // 旧方式（後方互換）
  targets?: LearningPlanTargetInput[] // 新方式（複合対象選択）
  frequency: PlanFrequency
  customIntervalDays?: number
  questionCount?: number
  difficulty?: TestDifficulty
  passingScore?: number
  startDate: string
  endDate: string
  testGenerationDaysBefore?: number
  requireApproval?: boolean
  autoPublish?: boolean
}

export interface UpdateLearningPlanRequest {
  title?: string
  description?: string
  questionCount?: number
  difficulty?: TestDifficulty
  passingScore?: number
  endDate?: string
  testGenerationDaysBefore?: number
  requireApproval?: boolean
  autoPublish?: boolean
}

export interface LearningPlanListFilter {
  status?: PlanStatus
  complianceRuleId?: string
  userGroupId?: string
  search?: string
  page?: number
  limit?: number
}

export interface LearningPlanListResponse {
  plans: LearningPlanResponse[]
  pagination: {
    page: number
    limit: number
    total: number
    pages: number
  }
}

// コンポーネント用エイリアス
export type ScheduledTest = ScheduledTestResponse

// 進捗管理用型
export interface LearningPlanProgress {
  totalTests: number
  completedTests: number
  pendingTests: number
  averageScore: number | null
  passRate: number | null
  participantStats: {
    completed: number
    inProgress: number
    notStarted: number
    failed: number
  }
}

export interface ParticipantProgress {
  userId: string
  userName: string
  completedTests: number
  averageScore: number | null
  passRate: number | null
  status: 'completed' | 'in_progress' | 'not_started' | 'failed'
  testResults: Array<{
    passed: boolean | null
    score: number | null
  }>
}
